import React, { useEffect, useState } from "react";
import styles from "./style.module.scss";
import baseStyles from "../../base.module.scss";
import TextInput from "../../../../../../components/form/text-input";
import { Select } from "../../../../../../components/form/select";
import Switch from "../../../../../../components/form/switch";
import NumberInput from "../../../../../../components/form/number-input";
import InfoIcon from "../../../../../../assets/svg/icons/common/info";
import useSalesFormStore from "../../../../../../stores/sales-form";
import { US_STATES } from "../../../../../../../constants";
import AddressAutocomplete from "../../../../../../components/form/address-autocomplete";
import { googlePlaceToFormData } from "../../../../../../utils/address-parser";
import ReactTooltip from "react-tooltip";

// Custom wrapper for Switch to better isolate click events
const SwitchWrapper = ({ id, name, label, checked, onChange }) => {
  return (
    <div className={styles.switchWrapper} style={{ width: 'auto', display: 'inline-flex' }}>
      <div style={{ width: 'auto', display: 'inline-flex' }}>
        <Switch
          id={id}
          name={name}
          label={label}
          checked={checked}
          onChange={onChange}
        />
      </div>
    </div>
  );
};

// Define options for dropdowns
const businessTypeOptions = [
  { value: "Sole Proprietor", label: "Sole Proprietor" },
  { value: "Partnership", label: "Partnership" },
  { value: "Corporation", label: "Corporation" },
  { value: "LLC", label: "LLC (single or multi-member)" },
];

const premiseTypeOptions = [
  { value: 1, label: "Commercial/Industrial" },
  { value: 2, label: "Kiosk" },
  { value: 3, label: "Mailbox" },
  { value: 4, label: "Office Building" },
  { value: 5, label: "Residential/Home Office" },
  { value: 6, label: "Retail Store Front" },
  { value: 7, label: "Mobile/On-the-Go" },
  { value: 8, label: "Website" },
];

// tooltip contents
const ToolTipLegalBusinessName = "This should be the legal name of your business as registered with the state. If applying as a sole proprietor, use your First and Last name.";
const ToolTipDBAStreet = "Enter the legal business address that you have registered with the state. This address should match your IRS form SS-4.";
const ToolTipBusinessStreet = "This address is where you're physically doing business (i.e. home address). This cannot be a PO Box.";
const ToolTipEIN = "You can find this number on your IRS Form-SS4. If you are a sole proprietor and haven't pulled an EIN/TAX ID you can use your SSN.";

/**
 * BusinessInformation Component
 * Renders a form for collecting business information including ownership, contact details, and addresses
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formik - Formik form instance containing values, errors, and handlers
 */
const BusinessInformation = ({ formik }) => {
  const { values, touched, errors, handleChange, setFieldValue } = formik;
  // console.log('values', values);
  const mccOptions = useSalesFormStore((state) => state.mccOptions);
  const [stateList, setStateList] = useState([]);

  /**
   * Initialize state list options on component mount
   */
  useEffect(() => {
    const stateOptions = Object.entries(US_STATES).map(([key, value]) => ({
      label: value,
      value: value
    }));
    setStateList(stateOptions);
  }, []);

  /**
   * Handles the "Same as DBA" toggle functionality
   * Copies or clears legal address fields based on toggle state
   * @param {boolean} checked - Whether the toggle is checked
   */
  const handleSameAsDBA = (checked) => {
    setFieldValue("same_as_dba", checked ? "yes" : "no");
    if (checked) {
      // Copy DBA address values to legal address fields
      setFieldValue("legal_address", values.dba_address);
      setFieldValue("legal_city", values.dba_city);
      setFieldValue("legal_state", values.dba_state);
      setFieldValue("legal_zip", values.dba_zip);
      setFieldValue("legal_phone", values.business_phone);
    }
  };

  const handleDbaAddressSelected = (place) => {
    // Parse the Google place data
    const addressData = googlePlaceToFormData(place, {
      streetAddress: 'dba_address',
      city: 'dba_city',
      state: 'dba_state',
      zipCode: 'dba_zip'
    });

    // Special handling for state to ensure it matches the dropdown options
    if (addressData.dba_state) {
      // Find matching state in the state list options
      const matchingState = stateList.find(option =>
        option.label === addressData.dba_state ||
        option.value === addressData.dba_state
      );

      if (matchingState) {
        addressData.dba_state = matchingState.value;
      } else {
        console.log('No matching state found in dropdown for:', addressData.dba_state);
      }
    }

    // Update the form fields
    Object.entries(addressData).forEach(([field, value]) => {
      console.log(`Setting ${field} to ${value}`);
      setFieldValue(field, value);
    });
  };

  const handleLegalAddressSelected = (place) => {
    // Parse the Google place data
    const addressData = googlePlaceToFormData(place, {
      streetAddress: 'legal_address',
      city: 'legal_city',
      state: 'legal_state',
      zipCode: 'legal_zip'
    });

    // Special handling for state to ensure it matches the dropdown options
    if (addressData.legal_state) {
      // Find matching state in the state list options
      const matchingState = stateList.find(option =>
        option.label === addressData.legal_state ||
        option.value === addressData.legal_state
      );

      if (matchingState) {
        addressData.legal_state = matchingState.value;
      } else {
        console.log('No matching state found in dropdown for:', addressData.legal_state);
      }
    }

    // Update the form fields
    Object.entries(addressData).forEach(([field, value]) => {
      setFieldValue(field, value);
    });
  };

  return (
    <div className={styles.container}>
      <ReactTooltip className={styles.tooltip} />

      <div className={styles.header}>
        <h4 className={styles.sectionTitle}>Business Information</h4>
      </div>

      <div className={styles.formContent}>
        {/* Business Information Form */}
        <div className={styles.formGrid}>
          {/* Type of Ownership */}
          <div className={styles.formGroup}>
            <Select
              label="Type of Ownership"
              placeholder="Select type"
              id="type_of_ownership"
              name="type_of_ownership"
              options={businessTypeOptions}
              value={values.type_of_ownership}
              onChange={(value) => {
                setFieldValue("type_of_ownership", value);
              }}
              error={touched.type_of_ownership && errors.type_of_ownership}
              placeholderAsBlankOption={true}
            />
          </div>

          {/* Legal Business Name */}
          <div className={styles.formGroup}>
            <TextInput
              label="Legal Business Name"
              placeholder="Legal Business Name"
              type="text"
              id="legal_business_name"
              name="legal_business_name"
              value={values.legal_business_name}
              onChange={handleChange}
              touched={touched.legal_business_name}
              errored={errors.legal_business_name}
              error={errors.legal_business_name}
              suffix={<InfoIcon 
                data-tip={ToolTipLegalBusinessName}
                data-place="top"
              />}
            />
          </div>

          {/* DBA Name */}
          <div className={styles.formGroup}>
            <TextInput
              label={<>DBA Name <span className={styles.optional}>(optional)</span></>}
              placeholder="DBA Name (if different from legal)"
              type="text"
              id="dba_name"
              name="dba_name"
              value={values.dba_name}
              onChange={handleChange}
              touched={touched.dba_name}
              errored={errors.dba_name}
              error={errors.dba_name}
            />
          </div>

          {/* Tax ID / EIN */}
          <div className={styles.formGroup}>
            <TextInput
              label="Tax ID / EIN"
              placeholder="Tax ID / EIN"
              type="text"
              id="tax_ein"
              name="tax_ein"
              value={values.tax_ein}
              touched={touched.tax_ein}
              errored={errors.tax_ein}
              error={errors.tax_ein}
              suffix={<InfoIcon 
                data-tip={ToolTipEIN}
                data-place="top"
              />}
              cleaveOptions={{
                blocks: [9],
                delimiter: "",
                numericOnly: true,
                onValueChanged: (e) => {
                  setFieldValue("tax_ein", e.target.value);
                },
              }}
            />
          </div>

          {/* Premise Type */}
          <div className={styles.formGroup}>
            <Select
              label="Premise Type"
              placeholder="Select Premise Type"
              id="premise_type"
              name="premise_type"
              options={premiseTypeOptions}
              value={values.premise_type}
              onChange={(value) => {
                setFieldValue("premise_type", value);
              }}
              error={touched.premise_type && errors.premise_type}
            />
          </div>

          {/* Years and Months of ownership */}
          <div className={styles.formGroup}>
            <div className={styles.inputsRow}>
              <div className={styles.halfWidth}>
                <NumberInput
                  label="Years of ownership"
                  placeholder="0"
                  type="text"
                  id="ownership_years"
                  name="ownership_years"
                  min={0}
                  max={99}
                  value={values.ownership_years}
                  onChange={(value) => setFieldValue("ownership_years", value)}
                  suffix="Years"
                  error={errors.ownership_years}
                  touched={touched.ownership_years}
                  errored={errors.ownership_years}
                />
              </div>
              <div className={styles.halfWidth}>
                <NumberInput
                  label="Months of ownership"
                  placeholder="0"
                  type="text"
                  id="ownership_months"
                  name="ownership_months"
                  min={0}
                  max={11}
                  value={values.ownership_months}
                  onChange={(value) => setFieldValue("ownership_months", value)}
                  suffix="Months"
                  error={errors.ownership_months}
                  touched={touched.ownership_months}
                  errored={errors.ownership_months}
                />
              </div>
            </div>
          </div>

          {/* Business Type and Tax exempt on the same row */}
          <div className={styles.formGroup}>
            <Select
              label="Business Type"
              placeholder="Select Business Type"
              id="business_type"
              name="business_type"
              search={true}
              options={mccOptions}
              value={values.business_type}
              onChange={(value) => {
                setFieldValue("business_type", value);
              }}
              error={touched.business_type && errors.business_type}
            />
          </div>

          {/* Tax exempt - with spacer div to prevent row-wide clicks */}
          <div className={styles.formGroup} style={{ display: 'flex' }}>
            <div className={styles.switchItem}>
              <SwitchWrapper
                id="tax_exempt"
                name="tax_exempt"
                label="Tax exempt"
                checked={values.tax_exempt == 'yes' ? true : false}
                onChange={({ target }) => setFieldValue("tax_exempt", target.checked ? 'yes' : 'no')}
              />
            </div>
            <div className={styles.clickBlocker}></div>
          </div>
        </div>

        {/* CONTACTS */}
        <div className={styles.sectionDivider}>
          <h5 className={styles.sectionLabel}>CONTACTS</h5>
          <div className={styles.dividerLine}></div>
        </div>

        <div className={styles.formGrid} style={{
          gridTemplateColumns: 'repeat(3, 1fr)'
        }}>
          {/* Business Phone Number */}
          <div className={styles.formGroup}>
            <TextInput
              label="Business Phone Number"
              placeholder="Business Phone Number"
              type="tel"
              id="business_phone"
              name="business_phone"
              value={values.business_phone}
              onChange={handleChange}
              touched={touched.business_phone}
              errored={errors.business_phone}
              error={errors.business_phone}
              cleaveOptions={{
                blocks: [3, 3, 4],
                delimiters: ["-", "-"],
                numericOnly: true,
                onValueChanged: (e) => {
                  setFieldValue("business_phone", e.target.value);
                },
              }}
            />
          </div>

          {/* Business Email */}
          <div className={styles.formGroup}>
            <TextInput
              label="Business Email"
              placeholder="Business Email"
              type="email"
              id="business_email"
              name="business_email"
              value={values.business_email}
              onChange={handleChange}
              touched={touched.business_email}
              errored={errors.business_email}
              error={errors.business_email}
            />
          </div>

          {/* Business Website */}
          <div className={styles.formGroup}>
            <TextInput
              label={<>Business Website URL <span className={styles.optional}>(optional)</span></>}
              placeholder="https://"
              type="url"
              id="business_website"
              name="business_website"
              value={values.business_website}
              onChange={handleChange}
              touched={touched.business_website}
              errored={errors.business_website}
              error={errors.business_website}
            />
          </div>
        </div>

        {/* DBA ADDRESS */}
        <div className={styles.sectionDivider}>
          <h5 className={styles.sectionLabel}>DBA ADDRESS</h5>
          <div className={styles.dividerLine}></div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* DBA Business Address - Full Width */}
          <div className={styles.formGroupFullWidth}>
            <AddressAutocomplete
              label="DBA Business Address"
              placeholder="DBA Business Address"
              id="dba_address"
              name="dba_address"
              value={values.dba_address}
              onChange={handleChange}
              onPlaceSelected={handleDbaAddressSelected}
              touched={touched.dba_address}
              errored={errors.dba_address}
              error={errors.dba_address}
            />
          </div>

          {/* DBA City, State, Zip Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px'
          }}>
            <TextInput
              label="DBA City"
              placeholder="City"
              type="text"
              id="dba_city"
              name="dba_city"
              value={values.dba_city}
              onChange={handleChange}
              touched={touched.dba_city}
              errored={errors.dba_city}
              error={errors.dba_city}
            />

            <Select
              label="DBA State"
              placeholder="Select state"
              id="dba_state"
              name="dba_state"
              options={stateList}
              value={values.dba_state}
              onChange={(value) => {
                setFieldValue("dba_state", value);
              }}
              error={touched.dba_state && errors.dba_state}
            />

            <TextInput
              label="DBA Zip"
              placeholder="Zip"
              type="text"
              id="dba_zip"
              name="dba_zip"
              value={values.dba_zip}
              onChange={handleChange}
              touched={touched.dba_zip}
              errored={errors.dba_zip}
              error={errors.dba_zip}
              suffix={<InfoIcon />}
              cleaveOptions={{
                blocks: [5],
                delimiter: "",
                numericOnly: true,
                onValueChanged: (e) => {
                  setFieldValue("dba_zip", e.target.value);
                },
              }}
            />

            <NumberInput
              label="Years at Location"
              id="location_years"
              name="location_years"
              min={0}
              max={99}
              value={values.location_years}
              onChange={(value) => setFieldValue("location_years", value)}
              suffix="Years"
            />

            <NumberInput
              label="Months at Location"
              id="location_months"
              name="location_months"
              min={0}
              max={11}
              value={values.location_months}
              onChange={(value) => setFieldValue("location_months", value)}
              suffix="Months"
            />

          </div>
        </div>

        {/* LEGAL BUSINESS ADDRESS */}
        <div className={styles.sectionDivider}>
          <h5 className={styles.sectionLabel}>LEGAL BUSINESS ADDRESS</h5>
          <div className={styles.dividerLine}></div>
        </div>

        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <Switch
              id="same_as_dba"
              name="same_as_dba"
              label="Same as DBA"
              checked={values.same_as_dba === "yes"}
              onChange={({ target }) => handleSameAsDBA(target.checked)}
            />
            <div className={styles.clickBlocker}></div>
          </div>
          <div className={styles.formGroup}></div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Legal Street Address - Full Width */}
          <div className={styles.formGroupFullWidth}>
            <TextInput
              label="Legal Street Address"
              placeholder="Legal Street Address"
              type="text"
              id="legal_address"
              name="legal_address"
              value={values.legal_address}
              onChange={handleChange}
              touched={touched.legal_address}
              errored={errors.legal_address}
              error={errors.legal_address}
              disabled={values.same_as_dba === "yes"}
            />
          </div>

          {/* Legal City, State, Zip Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px'
          }}>
            <TextInput
              label="Legal City"
              placeholder="City"
              type="text"
              id="legal_city"
              name="legal_city"
              value={values.legal_city}
              onChange={handleChange}
              touched={touched.legal_city}
              errored={errors.legal_city}
              error={errors.legal_city}
              disabled={values.same_as_dba === "yes"}
            />

            <Select
              label="Legal State"
              placeholder="Select state"
              id="legal_state"
              name="legal_state"
              options={stateList}
              value={values.legal_state}
              onChange={(value) => {
                setFieldValue("legal_state", value);
              }}
              error={touched.legal_state && errors.legal_state}
              disabled={values.same_as_dba === "yes"}
            />

            <TextInput
              label="Legal Zip"
              placeholder="Zip"
              type="text"
              id="legal_zip"
              name="legal_zip"
              value={values.legal_zip}
              onChange={handleChange}
              touched={touched.legal_zip}
              errored={errors.legal_zip}
              error={errors.legal_zip}
              disabled={values.same_as_dba === "yes"}
              cleaveOptions={{
                blocks: [5],
                delimiter: "",
                numericOnly: true,
                onValueChanged: (e) => {
                  setFieldValue("legal_zip", e.target.value);
                },
              }}
            />
          </div>

          {/* Legal Phone Number - 1/3 Width */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px'
          }}>
            <TextInput
              label="Legal Phone Number"
              placeholder="Legal Phone Number"
              type="tel"
              id="legal_phone"
              name="legal_phone"
              value={values.legal_phone}
              onChange={handleChange}
              touched={touched.legal_phone}
              errored={errors.legal_phone}
              error={errors.legal_phone}
              disabled={values.same_as_dba === "yes"}
              cleaveOptions={{
                blocks: [3, 3, 4],
                delimiters: ["-", "-"],
                numericOnly: true,
                onValueChanged: (e) => {
                  setFieldValue("legal_phone", e.target.value);
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessInformation; 